import { Grid, Typography, Button, createStyles, makeStyles, Theme, Paper, Card, CardActionArea } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { MerchantSummaryData } from '../custom-types/MerchantSummaryData';
import { PayerTransactionSummary, OrderDirection } from '../gql-types.generated';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    merchantGridCard: {
      minWidth: 390,
    },
    merchantCard: {
      padding: theme.spacing(2),
      textAlign: 'initial',
      border: '1px solid #CCCCCC',
      '&:hover': {
        border: '1px solid #276EF1',
      },
    },
    merchantName: {
      fontWeight: 500,
      minHeight: 78,
      maxHeight: 78,
      paddingTop: 10,
    },
    merchantLogo: {
      maxHeight: 75,
      maxWidth: 225,
    },
    merchantTotalDue: {
      fontWeight: 300,
    },
    merchantUnpaidInvoices: {
      fontWeight: 500,
      color: '#0000008A',
      marginBottom: 16,
    },
    mainGrid: {
      padding: theme.spacing(2),
      maxHeight: '92vh',
      overflow: 'auto',
    },
    sortList: {
      verticalAlign: 'middle',
    },
    doneIcon: {
      color: '#0275d8',
      fontSize: '1.3rem',
    },
    menuItem: {
      width: '280px',
      fontSize: '14px',
    },
    menuText: {
      whiteSpace: 'normal',
    },
    detailsButton: {
      '&:disabled': {
        color: '#276EF1',
        borderColor: '#276EF1',
        backgroundColor: 'transparent',
      },
    },
  }),
);

interface MerchantGridProps {
  payerTransactionSummaryByMerchant: PayerTransactionSummary | undefined;
  handleMerchantSelect: (id: string | undefined) => void;
  merchantDataSort: (merchantData: PayerTransactionSummary | undefined, order: OrderDirection) => PayerTransactionSummary | undefined;
  settingsMenuVisible: boolean;
}

const MerchantGrid: React.FC<MerchantGridProps> = props => {
  const { payerTransactionSummaryByMerchant, handleMerchantSelect, merchantDataSort, settingsMenuVisible } = props;
  const classes = useStyles();
  const [merchantDataList] = useState<PayerTransactionSummary | undefined>(
    merchantDataSort(payerTransactionSummaryByMerchant, OrderDirection.Asc),
  );
  const [merchantSummaryList, setMerchantSummaryList] = useState<MerchantSummaryData[] | undefined>();

  useEffect(() => {
    if (merchantDataList) {
      const summaryList: MerchantSummaryData[] = [];

      merchantDataList.merchantSummary.forEach(item => {
        const summaryItem: MerchantSummaryData = { merchantSummary: item, hasHover: false };
        summaryList.push(summaryItem);
      });

      setMerchantSummaryList(summaryList.length > 0 ? summaryList : undefined);
    }
  }, [merchantDataList]);

  const updateHover = (index: number, value: boolean) => {
    if (merchantSummaryList) {
      const merchantDataItems = [...merchantSummaryList];
      const merchantItem = { ...merchantDataItems[index] };
      merchantItem.hasHover = value;
      merchantDataItems[index] = merchantItem;

      setMerchantSummaryList(merchantDataItems);
    }
  };

  return (
    <Grid container className={classes.mainGrid}>
      <Grid item container spacing={2}>
        {merchantSummaryList &&
          merchantSummaryList.map((merchant, index) => {
            return (
              <Grid
                item
                lg={settingsMenuVisible ? 4 : 3}
                md={settingsMenuVisible ? 6 : 4}
                sm={settingsMenuVisible ? 12 : 6}
                xs={12}
                key={merchant.merchantSummary.merchantInfo?.id}
              >
                <Card variant="outlined">
                  <CardActionArea
                    onClick={() => {
                      handleMerchantSelect(merchant?.merchantSummary?.merchantInfo?.owner?.tenantId);
                    }}
                    onMouseEnter={() => {
                      if (merchant.merchantSummary.merchantInfo?.logoUrl) updateHover(index, true);
                    }}
                    onMouseLeave={() => {
                      if (merchant.merchantSummary.merchantInfo?.logoUrl) updateHover(index, false);
                    }}
                    data-cy="card-company-name"
                  >
                    <Paper className={classes.merchantCard}>
                      {(merchant?.merchantSummary.merchantInfo?.name || merchant?.merchantSummary.merchantInfo?.logoUrl) && (
                        <Grid item xs={12}>
                          {merchant?.merchantSummary.merchantInfo.logoUrl && !merchant.hasHover ? (
                            <img
                              className={classes.merchantLogo}
                              src={merchant.merchantSummary.merchantInfo.logoUrl}
                              data-cy="card-company-logo"
                            />
                          ) : (
                            <Typography variant="h5" className={classes.merchantName} noWrap>
                              {merchant.merchantSummary.merchantInfo?.name}
                            </Typography>
                          )}
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Balance Due
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h4" className={classes.merchantTotalDue} noWrap gutterBottom>
                          {new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: merchant?.merchantSummary.merchantInfo?.defaultCurrency || 'USD',
                            currencyDisplay: 'symbol',
                            maximumFractionDigits: 0,
                          }).format(merchant.merchantSummary.totalDue / 100)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          className={classes.merchantUnpaidInvoices}
                        >{`${merchant.merchantSummary.numberOfUnpaidRequests} Unpaid Invoices`}</Typography>
                      </Grid>
                      <Grid item container xs={12} justify="flex-end">
                        <Button color="primary" variant="text" size="medium" disabled className={classes.detailsButton}>
                          View Details
                        </Button>
                      </Grid>
                    </Paper>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
      </Grid>
    </Grid>
  );
};

export default MerchantGrid;
